import { secondaryButtonTokens } from '../../createBrandPreset.js';
import { bayhiveBase, bayhiveDark, bayhivePurple, bayhiveViolet } from '../shared.js';

// Purpose: Keeps BayHive's intentional action-state exceptions on Lara Buttons.
// Lara owns shared control geometry and typography.
const bayhiveFilledButton = (tone) => ({
  background: `light-dark({${tone}.700}, {${tone}.300})`,
  hoverBackground: `light-dark({${tone}.500}, {${tone}.200})`,
  activeBackground: `light-dark({${tone}.600}, {${tone}.100})`,
  borderColor: `light-dark({${tone}.700}, {${tone}.300})`,
  hoverBorderColor: `light-dark({${tone}.500}, {${tone}.200})`,
  activeBorderColor: `light-dark({${tone}.600}, {${tone}.100})`,
  color: `{${tone}.contrast.color}`,
  hoverColor: `{${tone}.contrast.color}`,
  activeColor: `{${tone}.contrast.color}`,
  focusRing: {
    color: 'transparent',
    shadow: tone === 'primary' || tone === 'info'
      ? `{${tone}.focus.ring.shadow}`
      : `0 0 0 0.2rem light-dark({${tone}.200}, color-mix(in srgb, {${tone}.400}, transparent 80%))`,
  },
});

const purpleInfoOutlinedButton = {
  hoverBackground: 'color-mix(in srgb, {info.color}, transparent 95%)',
  activeBackground: 'light-dark(color-mix(in srgb, {info.color}, transparent 90%), color-mix(in srgb, {info.color}, transparent 94%))',
  borderColor: '{info.color}',
  color: '{info.text.color}',
};

const purpleInfoTextButton = {
  hoverBackground: 'color-mix(in srgb, {info.color}, transparent 95%)',
  activeBackground: 'light-dark(color-mix(in srgb, {info.color}, transparent 90%), color-mix(in srgb, {info.color}, transparent 94%))',
  color: '{info.text.color}',
};

const secondaryLight = secondaryButtonTokens(bayhivePurple[700], 'light', bayhiveBase.white);
const secondaryDark = secondaryButtonTokens(bayhiveViolet[300], 'dark', bayhiveDark.content.background);
const highContrastSecondaryButton = {
  ...Object.fromEntries(
    Object.entries(secondaryLight).map(([token, value]) => [
      token,
      `light-dark(${value}, ${secondaryDark[token]})`,
    ]),
  ),
  focusRing: {
    color: 'transparent',
    shadow: '{form.field.focus.ring.shadow}',
  },
};

const highContrastSecondaryOutlinedButton = {
  hoverBackground: '{form.field.filled.hover.background}',
  activeBackground: 'color-mix(in srgb, {form.field.background}, {primary.color} 16%)',
  borderColor: '{text.muted.color}',
  hoverBorderColor: '{text.color}',
  activeBorderColor: '{text.hover.color}',
  color: '{text.color}',
  hoverColor: '{text.hover.color}',
  activeColor: '{text.hover.color}',
};

const highContrastSecondaryTextButton = {
  color: '{text.color}',
  hoverColor: '{text.hover.color}',
  activeColor: '{text.hover.color}',
};

export const buttonComponents = {
  button: {
    root: {
      primary: bayhiveFilledButton('primary'),
      secondary: {
        ...highContrastSecondaryButton,
      },
      success: bayhiveFilledButton('success'),
      info: bayhiveFilledButton('info'),
      warn: bayhiveFilledButton('warn'),
      danger: bayhiveFilledButton('danger'),
    },
    outlined: {
      primary: {
        borderColor: 'light-dark({primary.200}, {primary.color})',
        // Keep pressed text readable even on raised dark surfaces.
        activeBackground: 'light-dark({primary.100}, color-mix(in srgb, {primary.color}, transparent 94%))',
        color: '{primary.text.color}',
      },
      secondary: highContrastSecondaryOutlinedButton,
      info: purpleInfoOutlinedButton,
    },
    text: {
      primary: {
        activeBackground: 'light-dark({primary.100}, color-mix(in srgb, {primary.color}, transparent 94%))',
        color: '{primary.text.color}',
      },
      secondary: highContrastSecondaryTextButton,
      info: purpleInfoTextButton,
    },
    link: {
      color: '{primary.text.color}',
      hoverColor: '{primary.text.color}',
      activeColor: '{primary.text.color}',
    },
  },
  // Lara's small ToggleButton keeps its default .5rem vertical padding, unlike
  // its other small form controls. Map it to the shared small field metrics so
  // segmented choices remain 32px alongside small inputs.
  togglebutton: {
    root: {
      sm: {
        padding: '{form.field.sm.padding.y} {form.field.sm.padding.x}',
      },
    },
  },
};

export default buttonComponents;
